import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, X, FileText, CheckCircle2 } from 'lucide-react';
import axiosClient from '../../api/axiosClient';
import toast from 'react-hot-toast';

export default function ClientChatThread({ enquiry, onMarkAsRead }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChats();
  }, [enquiry.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chats]);

  const fetchChats = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/trials/me/enquiries/${enquiry.id}/chats`);
      setChats(res.data.chats || []);
      
      // If there are unread messages, we can mark as read here automatically if the backend allowed it,
      // but we still have the explicit button in the UI or we can just call it
      if (!enquiry.client_read) {
        onMarkAsRead(enquiry.id);
      }
    } catch (err) {
      console.error('Failed to load chat history', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() && !file) return;

    setSending(true);
    const formData = new FormData();
    if (message) formData.append('message', message);
    if (file) formData.append('file', file);

    try {
      const res = await axiosClient.post(`/trials/me/enquiries/${enquiry.id}/chats`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setChats(prev => [...prev, res.data.chat]);
      setMessage('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Original Enquiry */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">You wrote:</h4>
        <div className="bg-white p-4 rounded-xl border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
          {enquiry.message}
        </div>
      </div>
      
      {/* Legacy Response (if exists and no chats exist yet) */}
      {enquiry.response && chats.length === 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-primary-600 mb-2">Admin Reply:</h4>
          <div className="bg-primary-50 p-4 rounded-xl border border-primary-100 text-sm text-primary-900 whitespace-pre-wrap">
            {enquiry.response}
          </div>
        </div>
      )}

      {/* Chat Thread */}
      {chats.length > 0 && (
        <div className="space-y-4 pt-4 border-t border-slate-100">
          {chats.map(chat => {
            const isClient = chat.sender_type === 'client';
            return (
              <div key={chat.id} className={`flex flex-col ${isClient ? 'items-end' : 'items-start'}`}>
                <div className={`text-[10px] text-slate-400 mb-1 font-medium ${isClient ? 'mr-1' : 'ml-1'}`}>
                  {isClient ? 'You' : 'Admin'} • {new Date(chat.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
                <div className={`p-3 rounded-2xl text-sm max-w-[85%] whitespace-pre-wrap shadow-sm ${
                  isClient
                    ? 'bg-primary-600 text-white rounded-tr-sm'
                    : 'bg-white border border-slate-200 text-slate-700 rounded-tl-sm'
                }`}>
                  {chat.message}
                  
                  {chat.file_id && (
                    <a 
                      href={`http://localhost:4000/api/trials/me/enquiries/files/${chat.file_id}`} 
                      target="_blank" 
                      rel="noreferrer"
                      className={`mt-2 flex items-center gap-2 p-2 rounded-lg text-xs font-medium border ${
                        isClient 
                          ? 'bg-primary-700 border-primary-500 text-primary-50 hover:bg-primary-800' 
                          : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                      } transition-colors`}
                    >
                      <FileText size={14} />
                      {chat.file_name}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Loading State */}
      {loading && <div className="text-sm text-slate-400 text-center">Loading messages...</div>}

      {/* Reply Input */}
      <div className="pt-4 border-t border-slate-100">
        <form onSubmit={handleSend} className="flex flex-col gap-2">
          <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
            {file && (
              <div className="px-3 pt-2 pb-1 flex items-center justify-between bg-slate-50 border-b border-slate-100">
                <span className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <Paperclip size={12} /> {file.name}
                </span>
                <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-slate-400 hover:text-red-500">
                  <X size={14} />
                </button>
              </div>
            )}
            <textarea 
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type a reply..."
              className="w-full bg-transparent px-4 py-3 text-sm outline-none resize-none"
              rows="2"
            />
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden" 
              />
              <button 
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Paperclip size={16} /> Attach
              </button>
            </div>
            <button 
              type="submit"
              disabled={sending || (!message.trim() && !file)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg shadow-sm transition-colors flex items-center gap-2 text-sm font-medium"
            >
              Send <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
